<template>
  <div v-if="props.viewType === DataViewType.Grid" @click="openEditPackage()">
    <PackageCard
      :created-date="item.created_at"
      :package-name="item.package_name"
      :cover-image="coverImage"
      :total-videos="item.total_videos"
      :total-folders="item.total_folders"
      :publishers-count="item.publishers_count"
      :decline-count="item.decline_count"
      :categories="getCategories()"
      :package-type="DataType.Package"
      :dropdown-actions="actions"
      :orientations="item.orientations"
    />
  </div>
  <div
    v-if="props.viewType === DataViewType.List"
    id="package"
    ref="dataViewRow"
    class="package list"
    :style="`grid-template-columns:  ${columnsGridTemplate(props.columns)};`"
    @click="openEditPackage"
  >
    <div class="head" :class="shouldHideOnBreakpoint(props.columns, 'name')">
      <div class="title">
        <div>
          <img class="list-package-image" :src="coverImage" @error="onCoverImageError">
        </div>

        <div class="title-text">
          <NewTooltip :placement="'top'" :content="props.item.package_name" auto-ellipsis />
        </div>
      </div>
    </div>

    <div class="head" :class="shouldHideOnBreakpoint(props.columns, 'created_at')">
      <div class="columns-text">{{ getDateString(props.item.created_at) }}</div>
    </div>
    <div class="head" :class="shouldHideOnBreakpoint(props.columns, 'total_folders')">
      <div class="videos-amount">
        <div class="count-badge">
          {{ props.item.total_folders }}
        </div>
      </div>
    </div>
    <div class="head" :class="shouldHideOnBreakpoint(props.columns, 'total_videos')">
      <div class="videos-amount">
        <div class="count-badge">
          {{ props.item.total_videos }}
        </div>
      </div>
    </div>
    <div class="head" :class="shouldHideOnBreakpoint(props.columns, 'publishers_count')">
      <div class="videos-amount">
        <div class="count-badge">
          {{ props.item.publishers_count }}
        </div>
      </div>
    </div>
    <div class="head" :class="shouldHideOnBreakpoint(props.columns, 'decline_count')">
      <div class="videos-amount">
        <div class="count-badge">
          {{ props.item.decline_count ?? 0 }}
        </div>
      </div>
    </div>
    <ActionButtons :actions="actions" :is-visible="isRowHovered" :class="shouldHideOnBreakpoint(props.columns, 'actions')" />
  </div>
</template>

<script setup lang="ts">
import { computed, inject, ref } from 'vue'
import { useRouter } from 'vue-router'
import { useStore } from 'vuex'
import { useElementHover } from '@vueuse/core'
import { useI18n } from 'vue-i18n'
import { getDateString, openModalCustomConfirmation } from '@/services/utils'
import { CRUD, DataActions, DataType, DataViewType } from '@/interfaces'
import ActionButtons from '@/components/common/action-buttons/ActionButtons.vue'
import { columnsGridTemplate, shouldHideOnBreakpoint } from '@/services/layoutUtils'
import addVideosIconPlaceholder from '@/assets/playlists/playlist/add-videos-icon-placeholder.svg'
import { NewTooltip, PackageCard } from '@/components/common'
import { MY_PACKAGES_ACTIONS_INJECTION_KEY } from '@/constants'
import { PostPackagePayload } from '@/services/content/package/types'
import { apiService } from '@/services'
import { ModalType } from '@/components/modals/modals-util'
import { getCoverImage, onCoverImageError } from '@/components/content/packages/packageCoversUtils'
import { usePackageDistribution } from '@/composables/usePackageDistribution/usePackageDistribution'

defineEmits(['refreshRow'])

const image = new window.Image()
image.src = addVideosIconPlaceholder

const store = useStore()
const props = defineProps(['item', 'viewType', 'columns', 'detailsLink'])
const router = useRouter()
const { t } = useI18n()
const dataViewRow = ref()
const isRowHovered = useElementHover(dataViewRow)
const isEmptyPackage = computed(() => props.item.total_folders === 0 && props.item.total_videos === 0)

const packageForm = {
  packageName: ref(''),
  packageDescription: ref(''),
  packageTags: ref([]),
  selectedVideos: computed(() => []),
  selectedFolders: computed(() => []),
  selectedCoverImage: ref(null),
  uploadedCoverImages: ref({})
}

const { openDistributionModal } = usePackageDistribution({
  editMode: computed(() => false),
  newPackageId: ref(undefined),
  packageForm,
  updatePackage: async () => {},
  isEdited: () => false,
  packageId: ref(props.item.package_id)
})

const actions = computed(() => [
  {
    label: 'distribute',
    handler: openDistributionModal,
    translationKey: 'content-package-share'
  },
  {
    label: 'edit',
    handler: openEditPackage,
    translationKey: 'content-package-edit'
  },
  {
    label: 'duplicate',
    handler: openDuplicatedPackage,
    translationKey: 'content-package-duplicate',
    isDisabledWithTooltip: isEmptyPackage.value ? 'content-package-duplicate-empty-tooltip' : ''
  }
])
const coverImage = getCoverImage(props.item?.cover_image)
const modalType = ModalType.PACKAGE_DUPLICATE_MODAL
const dataActions: DataActions | undefined = inject<DataActions>(MY_PACKAGES_ACTIONS_INJECTION_KEY)
const onDuplicatePackage = async ({ inputValue }: { inputValue: string }) => {
  const item = await apiService.contentPackage.getContentPackage({ ...props.item }.package_id)
  const payload: PostPackagePayload = {
    package_name: inputValue,
    description: item.description,
    video_ids: item.video_ids || [],
    folder_ids: item.folder_ids || [],
    cover_image: item.cover_image,
    other_cover_images_paths: item.other_cover_images_paths || null,
    tags: item.tags ?? []
  }
  return apiService.contentPackage
    .postPackage(payload)
    .then(() => {
      store.dispatch('messages/addMessage', { message: t('content-package-duplicate-message-success'), type: 'success' })
      if (dataActions?.clearSearch) dataActions.clearSearch()
      if (dataActions?.resetToDefaultPageAndGetData) dataActions.resetToDefaultPageAndGetData()
    })
    .catch((error) => {
      store.dispatch('messages/addMessage', { message: error.response.data.error.description, type: 'error' })
    })
}

function openEditPackage() {
  router.push({
    path: `/packages/edit/${props.item.package_id}`,
    name: 'edit-package',
    params: { id: props.item.package_id }
  })
}

function openDuplicatedPackage() {
  openModalCustomConfirmation(
    {
      action: CRUD.Duplicate,
      type: DataType.Package,
      itemName: props.item.package_name,
      id: props.item.package_id,
      onConfirm: onDuplicatePackage
    },
    modalType
  )
}

function getCategories(): string[] {
  return props.item?.categories?.map((category: { id: number; name: string }) => category.id) ?? []
}
</script>

<style scoped lang="scss">
.nowrap {
  white-space: nowrap !important;
}

.date-section {
  display: flex;
  gap: 0.25rem;
  color: $secondaryTitleText;
}

.package {
  width: 100%;
  height: fit-content;
  cursor: pointer;

  .head {
    padding-left: 1rem;
    flex-direction: row;
    justify-content: flex-start;
    align-items: flex-start;
    display: grid;
    grid-template-columns: 100%;
    overflow: hidden;
  }

  &.list {
    width: 100%;
    display: grid;
    align-items: center;

    &:hover {
      background-color: #f2f4fa;
    }

    .icon {
      cursor: pointer;
    }

    .title {
      display: flex;
      align-items: center;
      gap: 1rem;
      color: #4c4c66;

      .list-package-image {
        width: 65px;
        height: 65px;
        object-fit: cover;
      }

      .title-text {
        display: grid;
        grid-template-columns: 100%;
        overflow: hidden;
        color: var(--body-text, #4c4c66);
        text-overflow: ellipsis;
        white-space: nowrap;
        font-size: 16px;
        font-weight: 600;
        letter-spacing: 0.1px;
      }
    }

    .columns-text {
      overflow: hidden;
      color: var(--body-text, #4c4c66);
      text-overflow: ellipsis;
      font-size: 14px;
      font-weight: 600;
      letter-spacing: 0.1px;
    }

    .count-badge {
      display: flex;
      width: 22px;
      height: 22px;
      justify-content: center;
      align-items: center;
      gap: 10px;
      border-radius: 30px;
      background: #dadbe8;
      border: 1px solid $inputBorder;
      border-radius: 30px;
      color: #4c4c66;
      text-align: center;
      font-size: 12px;
      font-weight: 600;
      margin-right: 8px;
    }

    .videos-amount {
      padding-left: 5px;
      display: flex;
      align-items: center;
      justify-content: flex-start;
      font-size: 16px;
      font-weight: 600;
      line-height: 22px;
      letter-spacing: 0.1;
      text-align: left;
      color: #4c4c66;
    }

    .date {
      font-size: 16px;
      font-weight: 600;
      line-height: 22px;
      letter-spacing: 0.1;
      text-align: left;
      color: #4c4c66;
    }

    .tags {
      display: flex;
      justify-content: flex-start;
      align-items: center;
      font-size: 12px;
      font-weight: 600;
      line-height: 16px;
      letter-spacing: 0px;
      text-align: center;
      color: #4c4c66;
      background: none;
      border-radius: 0;
      margin-right: 20px;
      padding: 0;
      gap: 8px;

      .videos-amount-number {
        display: flex;
        width: 27px;
        height: 26px;
        justify-content: center;
        align-items: center;
        gap: 10px;
        border-radius: 30px;
        background: $secondaryTitleText;
        border: 1px solid $secondaryTitleText;
        border-radius: 30px;
        color: $white;
        text-align: center;
        font-size: 12px;
        font-weight: 600;
      }

      .tag {
        padding: 3px 20px;
        background-color: #f2f4fa;
        border-radius: 4px;
        margin: 0;
        display: flex;
        max-width: fit-content;
      }
    }
  }

  .frame {
    border-radius: 0 0 6px 6px;

    .view {
      cursor: pointer;
      display: flex;
      flex-direction: row;
      justify-content: center;
      align-items: center;
      padding: 3.8px;
      font-weight: 700;
      font-size: 11px;
      line-height: 15px;
      color: #ffffff;
      position: absolute;
      width: 87px;
      height: 30px;
      left: 50%;
      top: 50%;
      transform: translate(-50%, -50%);
      opacity: 0;
      transition: opacity 0.3s;
    }

    .date {
      cursor: pointer;
      display: flex;
      flex-direction: row;
      justify-content: flex-start;
      align-items: flex-start;
      position: absolute;
      width: 87px;
      height: 30px;
      bottom: 0px;
      left: 15px;
      font-weight: 300;
      font-size: 14px;
      line-height: 19px;
      letter-spacing: 0.1px;
      color: #ffffff;

      img {
        margin-right: 4px;
      }
    }
  }
}

.thumbnails {
  width: 100%;
  padding-bottom: 60%;
  border-radius: 6px 6px 0 0;
  background: #f2f4fa;

  img.image {
    border-radius: 6px 6px 0 0;
  }

  &:hover {
    .view {
      opacity: 1;
    }
  }

  .info {
    top: 15px;
    left: 15px;
    position: absolute;

    .title {
      font-weight: 700;
      font-size: 18px;
      line-height: 25px;
      color: #ffffff;
      margin-bottom: 2px;
    }

    .params {
      font-weight: 300;
      font-size: 12px;
      line-height: 16px;
      letter-spacing: 0.1px;
      color: #ffffff;
      display: flex;
      justify-content: left;
      align-items: center;

      .dot {
        display: flex;
        width: 19px;
        justify-content: center;
        align-items: center;
      }
    }
  }

  &.list {
    width: 4.5rem;
    height: 4.5rem;
    padding-bottom: 0;
    border-radius: 6px;
    margin-right: 1rem;
    cursor: pointer;
  }
}

.row {
  display: flex;
  min-height: 60px;
  max-height: 60px;
  align-items: center;
}

.flex-between {
  justify-content: space-between;
  align-items: flex-start;
  position: relative;
}

.title {
  font-weight: 600;
  font-size: 14px;
  line-height: 19px;
  color: #7d81b3;
  margin: 16px 0;
}
</style>
